import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  getDocs,
  query,
  where,
  serverTimestamp,
  writeBatch,
  arrayUnion,
  arrayRemove,
  deleteField,
} from 'firebase/firestore';
import { db } from './config';
import { fetchActivePartners } from './herdPartners';

const horsesRef = collection(db, 'horses');

// Live list of horses the given user owns or has been shared (members array-contains uid),
// excluding horses that have been moved to the Recently Deleted trash. Sold horses
// are left out too — they live in the Sold archive — unless includeSold is set
// (billing still needs them while money is owed, and the backup export wants all).
export const subscribeToHorses = (uid, callback, { includeSold = false } = {}) => {
  if (!uid) { callback([]); return () => {}; }
  const q = query(horsesRef, where('members', 'array-contains', uid));
  return onSnapshot(q, (snapshot) => {
    const all = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(all.filter((h) => !h.deleted && (includeSold || !h.sold)));
  });
};

// Live list of horses in the Recently Deleted trash for the given user.
export const subscribeToDeletedHorses = (uid, callback) => {
  if (!uid) { callback([]); return () => {}; }
  const q = query(horsesRef, where('members', 'array-contains', uid));
  return onSnapshot(q, (snapshot) => {
    const all = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(all.filter((h) => h.deleted));
  });
};

// One-shot fetch of all horses for a user (for notification scheduling etc.)
export const fetchUserHorses = async (uid) => {
  if (!uid) return [];
  const q = query(horsesRef, where('members', 'array-contains', uid));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() })).filter((h) => !h.deleted && !h.sold);
};

// Live single horse document
export const subscribeToHorse = (horseId, callback) => {
  return onSnapshot(doc(db, 'horses', horseId), (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
};

export const addHorse = async (uid, horseData) => {
  // Herd partners are on every horse either of you adds, from the moment it
  // exists. A client horse — one whose creator is also its trainer — is not
  // part of the household herd and is left alone. Doing this here means every
  // way a horse can be created is covered, not just the Add Horse sheet.
  const isClientHorse = (horseData.trainers || []).some((t) => t?.uid === uid);
  let partners = [];
  try {
    partners = await fetchActivePartners(uid);
    // Only partners who share client horses too get onto a client horse.
    if (isClientHorse) partners = partners.filter((p) => p.includeClients);
  } catch (e) { /* the horse is still created; the sync catches up */ }
  const ref = await addDoc(horsesRef, {
    ...horseData,
    ownerId: uid,
    members: [uid, ...partners.map((p) => p.uid)],
    editors: partners.filter((p) => p.permission === 'edit').map((p) => p.uid),
    shares: partners.map((p) => ({ uid: p.uid, email: p.email || '', name: p.name || p.email || '', permission: p.permission })),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
};

export const updateHorse = (horseId, data) =>
  updateDoc(doc(db, 'horses', horseId), { ...data, updatedAt: serverTimestamp() });

// Move a horse to the Recently Deleted trash (recoverable).
// Records who deleted it so the trash and any later investigation can say.
export const softDeleteHorse = (horseId, byUid = null, byEmail = null) =>
  updateDoc(doc(db, 'horses', horseId), {
    deleted: true,
    deletedAt: serverTimestamp(),
    ...(byUid && { deletedBy: { uid: byUid, email: byEmail || '' } }),
    updatedAt: serverTimestamp(),
  });

// Restore a horse out of the Recently Deleted trash.
// deletedBy is intentionally kept for the audit trail.
export const restoreHorse = (horseId, byUid = null, byEmail = null) =>
  updateDoc(doc(db, 'horses', horseId), {
    deleted: false,
    deletedAt: null,
    ...(byUid && { restoredBy: { uid: byUid, email: byEmail || '' } }),
    updatedAt: serverTimestamp(),
  });

// Mark a horse sold without transferring it: it leaves the herd, its tasks and
// reminders stop, and everything about it stays readable in the Sold archive.
// sale: { soldDate (YYYY-MM-DD, required), soldTo, salePrice (number|null), soldNotes }
export const markHorseSold = (horseId, sale, byUid = null, byEmail = null) =>
  updateDoc(doc(db, 'horses', horseId), {
    sold: true,
    soldDate: sale.soldDate,
    soldTo: sale.soldTo || '',
    salePrice: sale.salePrice ?? null,
    soldNotes: sale.soldNotes || '',
    soldAt: serverTimestamp(),
    ...(byUid && { soldBy: { uid: byUid, email: byEmail || '' } }),
    updatedAt: serverTimestamp(),
  });

// Undo a sale. The sale details are kept on the record (only the flag clears)
// so a mistaken undo loses nothing.
export const unmarkHorseSold = (horseId) =>
  updateDoc(doc(db, 'horses', horseId), { sold: false, updatedAt: serverTimestamp() });

// Permanently remove a horse (used for "Delete Forever" and trash auto-purge).
export const deleteHorse = (horseId) => deleteDoc(doc(db, 'horses', horseId));

// Share a horse with another user by UID.
// currentShares is the horse's existing shares array (horse.shares || []).
// For trainer permission, pass ownerContact = { name, email, phone }
export const shareHorse = (horseId, currentShares, toUid, toEmail, toName, permission, ownerContact = null, canSeeBilling = false) => {
  const newShare = { uid: toUid, email: toEmail, name: toName || toEmail, permission };
  const hasContact = ownerContact && (ownerContact.name || ownerContact.email || ownerContact.phone);
  return updateDoc(doc(db, 'horses', horseId), {
    members: arrayUnion(toUid),
    ...((permission === 'edit' || permission === 'trainer') && { editors: arrayUnion(toUid) }),
    shares: [...currentShares.filter((s) => s.uid !== toUid), newShare],
    // A horse can have multiple trainers — add to the trainers array.
    ...(permission === 'trainer' && {
      trainers: arrayUnion({ uid: toUid, email: toEmail, name: toName || toEmail }),
      ...(hasContact && { ownerContact }),
    }),
    // Optionally let a view/edit share see the billing ledger.
    ...(canSeeBilling && { ledgerViewers: arrayUnion(toUid) }),
    updatedAt: serverTimestamp(),
  });
};

// Remove a user's access to a horse. If they were a trainer, pass the horse's
// current trainers array so we can drop them from it (supports multiple trainers).
export const revokeShare = (horseId, currentShares, shareUid, currentTrainers = null) => {
  const updated = currentShares.filter((s) => s.uid !== shareUid);
  return updateDoc(doc(db, 'horses', horseId), {
    members: arrayRemove(shareUid),
    editors: arrayRemove(shareUid),
    ledgerViewers: arrayRemove(shareUid),
    shares: updated,
    ...(Array.isArray(currentTrainers) && {
      trainers: currentTrainers.filter((t) => t.uid !== shareUid),
    }),
    // Clear any legacy single-trainer field if the removed user matches it.
    ...(currentTrainers && { trainerInfo: deleteField() }),
    updatedAt: serverTimestamp(),
  });
};

// A trainer requests to add another trainer — creates a pending request the
// owner must approve. Does NOT grant access until approved.
export const requestTrainer = (horseId, toUid, toEmail, toName, requestedByName) =>
  updateDoc(doc(db, 'horses', horseId), {
    pendingTrainers: arrayUnion({ uid: toUid, email: toEmail, name: toName || toEmail, requestedByName: requestedByName || '' }),
    updatedAt: serverTimestamp(),
  });

// Owner approves a pending trainer — grants access and moves them into trainers.
export const approveTrainer = (horseId, currentShares, pendingTrainers, req) =>
  updateDoc(doc(db, 'horses', horseId), {
    members: arrayUnion(req.uid),
    editors: arrayUnion(req.uid),
    trainers: arrayUnion({ uid: req.uid, email: req.email, name: req.name }),
    shares: [...currentShares.filter((s) => s.uid !== req.uid), { uid: req.uid, email: req.email, name: req.name, permission: 'trainer' }],
    pendingTrainers: pendingTrainers.filter((p) => p.uid !== req.uid),
    updatedAt: serverTimestamp(),
  });

// A company colleague joins one of my client horses as a Provider. The share
// is tagged with the company so that, if they later leave it, only the access
// the company gave them is taken back — a share someone made by hand stays.
export const grantCompanyTrainer = (horseId, currentShares, member, companyId, companyName = '') =>
  updateDoc(doc(db, 'horses', horseId), {
    members: arrayUnion(member.uid),
    editors: arrayUnion(member.uid),
    trainers: arrayUnion({ uid: member.uid, email: member.email || '', name: member.name || member.email || '' }),
    shares: [
      ...currentShares.filter((s) => s.uid !== member.uid),
      { uid: member.uid, email: member.email || '', name: member.name || member.email || '', permission: 'trainer', viaCompany: companyId, viaCompanyName: companyName || '' },
    ],
    updatedAt: serverTimestamp(),
  });

// Owner denies a pending trainer request.
export const denyTrainer = (horseId, pendingTrainers, reqUid) =>
  updateDoc(doc(db, 'horses', horseId), {
    pendingTrainers: pendingTrainers.filter((p) => p.uid !== reqUid),
    updatedAt: serverTimestamp(),
  });

// Transfer ownership of a horse to another user.
// opts.keepAsTrainer: the person handing off stays on as a trainer (member +
//   editor + trainers) instead of losing access — used when a trainer transfers
//   a client horse to its owner. opts.currentTrainers/fromInfo keep the trainers
//   array consistent (no stale/duplicate entries).
export const transferHorse = (horseId, toUid, fromUid, currentShares, opts = {}) => {
  const { keepAsTrainer = false, currentTrainers = [], fromInfo = null } = opts;
  if (keepAsTrainer) {
    const trainers = currentTrainers.slice();
    if (fromInfo && !trainers.some((t) => t.uid === fromUid)) trainers.push(fromInfo);
    const info = fromInfo || { uid: fromUid, email: '', name: '' };
    return updateDoc(doc(db, 'horses', horseId), {
      ownerId: toUid,
      members: arrayUnion(toUid),                 // new owner joins (if not already)
      editors: arrayUnion(fromUid),               // transferrer keeps edit/billing access
      trainers,
      shares: [...currentShares.filter((s) => s.uid !== toUid && s.uid !== fromUid), { uid: fromUid, email: info.email, name: info.name || info.email, permission: 'trainer' }],
      updatedAt: serverTimestamp(),
    });
  }
  return updateDoc(doc(db, 'horses', horseId), {
    ownerId: toUid,
    members: arrayRemove(fromUid),
    editors: arrayRemove(fromUid),
    trainers: currentTrainers.filter((t) => t.uid !== fromUid), // clean up any stale entry
    shares: currentShares.filter((s) => s.uid !== toUid && s.uid !== fromUid),
    updatedAt: serverTimestamp(),
  });
};

// Pictures & Documents section: attach an uploaded file's metadata to the horse.
// `docMeta` = { url, path, name, mime, docType, uploadedBy, uploadedAt }.
export const addHorseDocument = (horseId, docMeta) =>
  updateDoc(doc(db, 'horses', horseId), {
    documents: arrayUnion(docMeta),
    updatedAt: serverTimestamp(),
  });

// Remove a document entry from the horse (the storage file is deleted by the caller).
export const removeHorseDocument = (horseId, docMeta) =>
  updateDoc(doc(db, 'horses', horseId), {
    documents: arrayRemove(docMeta),
    updatedAt: serverTimestamp(),
  });

// Bulk import: writes each horse as a new document owned by uid
export const importHorses = async (uid, horsesArray) => {
  const batch = writeBatch(db);
  horsesArray.forEach((h) => {
    const { id, ...rest } = h;
    const ref = doc(horsesRef);
    batch.set(ref, {
      ...rest,
      members: [uid],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();
};
